/**
 * Share Guard
 *
 * Validates magic link (magicId + password) and provides share context
 * Shows error if link is invalid or expired
 */

import type { ReactNode } from "react"
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { validateMagicLink } from "@/lib/api/share";
import { ShareProvider } from "@/lib/contexts/ShareContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { HugeiconsIcon } from "@hugeicons/react"
import { AlertCircleIcon } from "@hugeicons/core-free-icons"

export function ShareGuard({ children }: { children: ReactNode }) {
  const { magicId, password } = useParams<{ magicId: string; password: string }>();

  const {
    data: validationResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["magicLink", magicId, password],
    queryFn: () => validateMagicLink(magicId!, password!),
    enabled: !!magicId && !!password,
    retry: false,
    staleTime: 60 * 60 * 1000, // 60 minutes (token expires in 90 minutes)
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent mb-4"></div>
          <p className="text-muted-foreground">Validating share link...</p>
        </div>
      </div>
    );
  }

  if (error || !validationResponse) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <div className="flex items-center gap-2">
              <HugeiconsIcon icon={AlertCircleIcon} size={20} className="text-destructive" />
              <CardTitle>Invalid share link</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              This share link is invalid or has expired. Please request a new
              link from the dashboard owner.
            </p>
            <div className="flex gap-2">
              <Button asChild variant="default">
                <Link to="/sign-in">Sign in</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/sign-up">Create account</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <ShareProvider
      value={{
        magicId: magicId!,
        password: password!,
        token: validationResponse.token,
        propertyId: validationResponse.property.propertyId,
        property: validationResponse.property,
      }}
    >
      {children}
    </ShareProvider>
  );
}
